import type { PostHogSurveyQuestion } from "../integrations/analytics/client";

/**
 * Maps a numeric rating response to the semantic bucket PostHog uses as the
 * responseValues key. Must exactly match PostHog's own bucketing logic:
 * https://github.com/PostHog/posthog-js (surveys-preview.js `at` function)
 */
export const getRatingBucket = (value: number, scale: number): string => {
  switch (scale) {
    case 2:
      return value === 1 ? "negative" : "positive";
    case 3:
      return value === 1 ? "negative" : value === 2 ? "neutral" : "positive";
    case 5:
      return value > 2 ? (value === 3 ? "neutral" : "positive") : "negative";
    case 7:
      return value > 3 ? (value === 4 ? "neutral" : "positive") : "negative";
    case 10:
      // NPS: 0-6 detractors, 7-8 passives, 9-10 promoters
      return value > 6 ? (value > 8 ? "promoters" : "passives") : "detractors";
    default:
      return "positive";
  }
};

/**
 * Resolves the next question index (or "end") for a PostHog survey question,
 * faithfully replicating the logic in posthog-js surveys-preview.js `getNextSurveyStep`.
 *
 * Key differences from a naïve implementation:
 * - Rating `responseValues` keys are semantic buckets ("promoters", "passives",
 *   "detractors", "positive", "neutral", "negative") — NOT the raw number.
 * - SingleChoice `responseValues` keys are the numeric index of the chosen option
 *   in the `choices` array — NOT the choice string itself.
 */
export const getNextStepIndex = (
  questions: PostHogSurveyQuestion[],
  currentIndex: number,
  responseValue: unknown
): number | "end" => {
  const currentQ = questions[currentIndex];
  const nextIndex = currentIndex + 1;
  const isLastQuestion = currentIndex === questions.length - 1;

  // No branching configured — sequential or end
  if (!currentQ || !currentQ.branching) {
    return isLastQuestion ? "end" : nextIndex;
  }

  const branching = currentQ.branching as {
    type: string;
    index?: number;
    responseValues?: Record<string, unknown>;
  };

  // Explicit end
  if (branching.type === "end") {
    return "end";
  }

  // Jump to specific question
  if (branching.type === "specific_question") {
    if (typeof branching.index === "number" && Number.isInteger(branching.index)) {
      return branching.index;
    }
  }

  // Response-based branching
  if (branching.type === "response_based" && branching.responseValues) {
    const rv = branching.responseValues;

    if (currentQ.type === "rating") {
      const scale = currentQ.scale ?? 5;
      const numVal = typeof responseValue === "number" ? responseValue : Number(responseValue);
      if (!Number.isInteger(numVal)) {
        return nextIndex;
      }
      const bucket = getRatingBucket(numVal, scale);
      if (Object.prototype.hasOwnProperty.call(rv, bucket)) {
        const target = rv[bucket];
        if (typeof target === "number" && Number.isInteger(target)) return target;
        if (target === "end") return "end";
      }
      return nextIndex;
    }

    if (currentQ.type === "single_choice") {
      const choices = currentQ.choices ?? [];
      let choiceIndex = choices.indexOf(String(responseValue));
      // PostHog treats an unmatched response as "other" (last open choice)
      if (choiceIndex === -1 && (currentQ as { hasOpenChoice?: boolean }).hasOpenChoice) {
        choiceIndex = choices.length - 1;
      }
      if (choiceIndex !== -1 && Object.prototype.hasOwnProperty.call(rv, choiceIndex)) {
        const target = rv[choiceIndex];
        if (typeof target === "number" && Number.isInteger(target)) return target;
        if (target === "end") return "end";
      }
      return nextIndex;
    }

    // Other question types fall through to next
    return nextIndex;
  }

  // Unknown branching type — fall back to sequential
  return isLastQuestion ? "end" : nextIndex;
};
